import { useI18n } from '@ecom/i18n';
import { Checkbox, Field, Input, Select } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { categoryFormSchema, type CategoryFormValues } from '../schemas';
import type { Category } from '../types';

import { slugify } from '@/features/store/slug';
import { whenDefined } from '@/lib/optional-props';

const EMPTY_VALUES: CategoryFormValues = {
  name: '',
  slug: '',
  description: null,
  imageUrl: null,
  parentCategoryId: null,
  isActive: true,
};

/** A real, previously-saved category back into form values, for the edit modal. */
export function categoryToFormValues(category: Category): CategoryFormValues {
  return {
    name: category.name,
    slug: category.slug,
    description: category.description,
    imageUrl: category.imageUrl,
    parentCategoryId: category.parentCategoryId,
    isActive: category.isActive,
  };
}

/**
 * Create AND edit share this one form — `PUT` is a full-state replace,
 * the same posture `ProductForm`/`CouponForm` already take. `formId` lets
 * the caller wire a `Modal` footer's submit `Button` to `form={formId}`.
 *
 * The parent-category options EXCLUDE the category being edited (a
 * category cannot be its own parent — `categories_not_own_parent`,
 * migration 0018 — so offering itself in the list would only ever
 * produce a rejected submit). Deeper cycles (A's parent is B, B's parent
 * is A) are not guarded against here, the same documented, deliberate gap
 * `CategoryRepository`'s own header leaves to the database's direct-
 * parent CHECK alone — this form invents no stricter rule on top of it.
 */
export function CategoryForm({
  formId,
  categories,
  editingCategoryId,
  defaultValues,
  onSubmit,
  isSubmitting,
}: {
  formId: string;
  categories: readonly Category[];
  editingCategoryId?: string;
  defaultValues?: CategoryFormValues;
  onSubmit: (values: CategoryFormValues) => void;
  isSubmitting: boolean;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: defaultValues ?? EMPTY_VALUES,
  });
  // A new category's slug follows its name (`StoreForm`'s rule) until the merchant types one; an existing slug is a
  // live public address and is never rewritten from a rename.
  const slugFollowsName = useRef((defaultValues?.slug ?? '') === '');

  const parentOptions = categories
    .filter((category) => category.id !== editingCategoryId)
    .map((category) => ({ value: category.id, label: category.name }));

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={isSubmitting} className="contents">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.categories.form.nameLabel')}
            {...whenDefined('error', errors.name?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('name', {
                  onChange: (event: { target: { value: string } }) => {
                    if (slugFollowsName.current) {
                      setValue('slug', slugify(event.target.value), {
                        shouldValidate: isSubmitted,
                      });
                    }
                  },
                })}
                autoFocus
                maxLength={200}
              />
            )}
          </Field>

          <Field
            label={t('merchant.store.form.slugLabel')}
            hint={t('merchant.categories.form.slugHint')}
            {...whenDefined('error', errors.slug?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('slug', {
                  onChange: () => {
                    slugFollowsName.current = false;
                  },
                })}
              />
            )}
          </Field>
        </div>

        <Field
          label={t('merchant.store.form.descriptionLabel')}
          optional
          {...whenDefined('error', errors.description?.message)}
        >
          {(bind) => (
            <textarea {...bind} {...register('description')} rows={3} className="ecom-input" />
          )}
        </Field>

        <Field
          label={t('merchant.categories.form.imageLabel')}
          optional
          hint={t('merchant.categories.form.imageHint')}
          {...whenDefined('error', errors.imageUrl?.message)}
        >
          {(bind) => (
            <Input {...bind} {...register('imageUrl')} type="url" placeholder="https://…" />
          )}
        </Field>

        <Field
          label={t('merchant.categories.form.parentLabel')}
          optional
          hint={t('merchant.categories.form.parentHint')}
          {...whenDefined('error', errors.parentCategoryId?.message)}
        >
          {(bind) => <Select {...bind} {...register('parentCategoryId')} options={parentOptions} />}
        </Field>

        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <Checkbox
              label={t('merchant.categories.form.activeCheckbox')}
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </fieldset>
    </form>
  );
}
